/** The words for a person's namesakes (#454, 28 Oct): the other records with their name, possible matches, never merged. */
import type { MasterStatus, NamesakeRole, PossibleMatch } from "../../../api/audienceReview.types";
import { STANDING_LABEL } from "../../kit";
import { standingOf } from "./format";

const ROLE: Readonly<Record<NamesakeRole, string>> = { doctor: "Doctor", ca: "CA", student: "Student", spouse: "Spouse", vendor: "Vendor", other: "Other" };

/**
 * "Doctor", "Vendor, Lakeshore Spine Supply", "Doctor and vendor, Lakeshore Spine Supply": a record that vends names its
 * company (the server sends one only then); a CE21 record under another name, linked to a contact with this one, says so.
 */
export function roleWords({ role, company }: Pick<PossibleMatch, "role" | "company">): string {
  if (!role) return "CE21 record, another name";
  const word = ROLE[role] ?? ROLE.other;
  if (!company) return word;
  return role === "vendor" ? `Vendor, ${company}` : `${word} and vendor, ${company}`;
}

/** A CE21 standing as the lists say it ("Non-member"), or "" when there is none. */
export function standingWords(standing: string): string {
  const key = standingOf(standing);
  return key ? STANDING_LABEL[key] : "";
}

const AC: Readonly<Record<string, string>> = { subscribed: "subscribed", unsubscribed: "unsubscribed", cleaned: "bounced", not_on_master: "not on the master list" };

/** A contact's master-list status, lower case: "unsubscribed", "not on the master list". */
export const acWords = (status: MasterStatus | ""): string => AC[status] ?? status.replace(/_/g, " ");

/** The held row's "Same name": each CE21 record the hold compared the person with (role, standing, and its ActiveCampaign status when it is the contact the name found), then the contacts no CE21 record has. */
export function matchesText(matches: readonly PossibleMatch[]): string {
  const parts = matches.flatMap((m) => {
    if (m.id === null) return [];
    const facts = [standingWords(m.standing).toLowerCase(), m.activeCampaign && `in ActiveCampaign ${acWords(m.activeCampaign)}`].filter(Boolean);
    return [facts.length > 0 ? `${roleWords(m)} (${facts.join(", ")})` : roleWords(m)];
  });
  const alone = matches.filter((m) => m.id === null);
  if (alone.length > 0) {
    const statuses = alone.flatMap((m) => (m.activeCampaign ? [acWords(m.activeCampaign)] : []));
    parts.push(`${alone.length === 1 ? "1 contact" : `${alone.length} contacts`} in ActiveCampaign only${statuses.length > 0 ? `: ${statuses.join("; ")}` : ""}`);
  }
  return parts.join(" · ");
}
